// @ts-check
// ── Settings > Integrations: the Facebook and Instagram ads card ─────────────
//
// Ad data plan Step 4 (docs/superpowers/specs/2026-10-27-meta-ad-account-design.md). The coach connects his own Meta
// ad account here, and every morning FitOps reads what each ad spent (cron-ad-numbers.js). Approved prototype:
// mockups/meta-ads-card-2026-10-27.html, states 1 to 5; the markup and the words below are the prototype's, except the
// state 1 hint: Meta's login for a new app has no step to pick an ad account (10/27), so it names the Facebook account.
//
// The card picks its state from one answer, meta-ads-status:
//   1. no ad account: Connect your ad account (the button is off, and says why, while the ads app is not set up in
//      FitOps Admin);
//   2. the history is still coming in: a spinner line;
//   3. connected: when the numbers came in and what the ads spent in the last 90 days;
//   4. 7 days or fewer left on the login: log in by that day;
//   5. the login stopped (status reconnect, or a login already past its day): log in again.
//
// ⚠️ HIDDEN UNLESS THE SERVER SAYS available. Only the accounts the FitOps Admin switch lets in see it (the platform
// owner until Meta reviews the ads app); any other coach would press a button that only leads to a Meta error page. A
// team member is refused the status (owner only), which leaves it hidden too. The front end has no rule of its own.
//
// ⚠️ THE POPUP OPENS IN THE CLICK, BEFORE ANY AWAIT. A browser may block a window opened after the click's own tick,
// so it opens blank first and is sent to Facebook once meta-ads-connect-start answers.
//
// ⚠️ ONLY OUR OWN SITE IS LISTENED TO. Any page can post a message to this window; the callback page posts to our
// origin only (meta-ads-connect-callback.js), and this listener checks that it came from there.
//
// THE GUIDE (10/28, docs/superpowers/specs/2026-10-28-meta-ads-for-every-coach-design.md; approved prototype
// mockups/meta-ads-guide-2026-10-28.html, states g1, g2, g2-pick, g2-none, g3 and done). State 1 is its step 1. Steps 2
// (the sales call link and the Meta line for every ad) and 3 (two things to watch for) open right after a connect from
// state 1, and again from "How to set up your ads" on the Connected state. Nothing is stored: a reload shows the
// Connected state with that link.
//
// ⚠️ THE GUIDE NEVER HIDES "LOG IN AGAIN". It draws over states 2 and 3 only; states 4 and 5 close it.
//
// ⚠️ THE OPEN STEP LIVES HERE, NOT IN THE PAGE. The card is drawn again from scratch every 20 seconds while the past
// numbers come in, and whenever Settings opens on Integrations, so cardHtml draws the open step each time. Coming back
// from Booked therefore lands on step 2, with the link just made showing.
//
// ⚠️ BOOKED LOADS ONLY WHEN STEP 2 OPENS (dynamic import), so Settings' start-up does not pull Booked in.

import { callFunction } from './api.js';
import { showToast, showConfirm } from './utils.js';
import { getCoachTimezone } from './config.js';
import { money, fmt, longDay } from './coach-dashboard/words.js';
import { copyLine } from './copy-line.js';
import { AD_LINES } from './lead-engine/ad-lines.js';

const DAY_MS = 86_400_000;
/** "Log in again" turns on with this long or less left on the login. */
const WARN_MS = 7 * DAY_MS;
/** While the history comes in, the card asks again this often, so it turns to Connected without a reload. */
const HISTORY_RECHECK_MS = 20_000;

const esc = (/** @type {unknown} */ s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => /** @type {Record<string, string>} */ ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
const ACTIONS = '<div class="settings-actions" style="margin-top:16px;gap:8px;display:flex;">';
const DISCONNECT = '<button class="btn-secondary mad-end" data-mad="disconnect">Disconnect</button>';

// The guide's icons: Booked's copy and open (booked/icons.js, written here so the card does not load Booked), and the
// two in step 3 drawn in the same stroke.
/** @param {string} path */
const icon = (path) => `<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
const ICON = {
  copy: icon('<rect x="7" y="7" width="9" height="9" rx="2"/><path d="M13 5.5A1.5 1.5 0 0 0 11.5 4h-6A1.5 1.5 0 0 0 4 5.5v6A1.5 1.5 0 0 0 5.5 13"/>'),
  external: icon('<path d="M11 4h5v5"/><path d="M16 4l-7 7"/><path d="M14 12v3.5A1.5 1.5 0 0 1 12.5 17h-8A1.5 1.5 0 0 1 3 15.5v-8A1.5 1.5 0 0 1 4.5 6H8"/>'),
  boost: icon('<path d="M3.5 8.2v3.6a1 1 0 0 0 1 1h1.8l5.2 3.4V3.8L6.3 7.2H4.5a1 1 0 0 0-1 1z"/><path d="M14.2 7.4a3.6 3.6 0 0 1 0 5.2"/><path d="M6.6 12.8l.9 3.7"/>'),
  people: icon('<circle cx="7.5" cy="7" r="2.6"/><path d="M2.8 16a4.7 4.7 0 0 1 9.4 0"/><path d="M12.6 4.6a2.6 2.6 0 0 1 0 4.8"/><path d="M14.6 11.6a4.7 4.7 0 0 1 2.6 4.4"/>'),
};

/** @param {string} pillClass @param {string} pillWords */
function header(pillClass, pillWords) {
  return '<div class="card-header">'
    // tabindex -1: a step changed by a press, or Done, takes the focus to a title, so a screen reader hears where it is.
    + '<h2 id="meta-ads-title" tabindex="-1" style="outline:none">Facebook and Instagram ads</h2>'
    + `<span class="email-domain-status ${pillClass}" id="meta-ads-card-status" style="margin-left:auto;">${pillWords}</span>`
    + '</div>';
}

/** @param {string} label @param {string} value already escaped @param {string} [sub] already escaped */
function row(label, value, sub) {
  return '<div class="settings-row">'
    + `<div class="settings-row-label"><span class="settings-row-title">${label}</span></div>`
    + `<div class="settings-row-control mad-value">${value}${sub ? `<span class="settings-row-sub">${sub}</span>` : ''}</div>`
    + '</div>';
}

/** @param {any} a @param {number} now */
const expiresAt = (a, now) => {
  const t = a.tokenExpiresAt ? new Date(a.tokenExpiresAt).getTime() : NaN;
  return Number.isFinite(t) ? t - now : Infinity;
};

/**
 * Which of the prototype's five states. A login that has stopped, or whose day has already passed, is state 5 before
 * anything else: no history can come in on it, and "log in by" a day gone by would be wrong.
 * @param {any} s the status @param {number} now
 * @returns {1 | 2 | 3 | 4 | 5}
 */
export function stateFor(s, now) {
  const accounts = (s && s.accounts) || [];
  if (!accounts.length) return 1;
  if (accounts.some((/** @type {any} */ a) => a.status === 'reconnect' || expiresAt(a, now) <= 0)) return 5;
  if (accounts.some((/** @type {any} */ a) => !a.historyIn)) return 2;
  if (accounts.some((/** @type {any} */ a) => expiresAt(a, now) <= WARN_MS)) return 4;
  return 3;
}

/** 'YYYY-MM-DD' of an instant, in the coach's zone. @param {Date} t @param {string} tz */
const dayIn = (t, tz) => t.toLocaleDateString('en-CA', { timeZone: tz });

/**
 * When the numbers last came in, the prototype's way: "Today at 6:10 AM", "October 21 at 6:10 AM".
 * @param {string} at @param {string} tz @param {number} now
 */
function updatedWords(at, tz, now) {
  const t = new Date(at);
  if (!Number.isFinite(t.getTime())) return '';
  const time = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).replace(/\s+/g, ' ');
  const day = dayIn(t, tz);
  return day === dayIn(new Date(now), tz) ? `Today at ${time}` : `${longDay(day)} at ${time}`;
}

/** The newest time among these accounts' last pulls. @param {any[]} accounts */
const lastPulled = (accounts) => accounts.map((a) => a.lastPulledAt).filter(Boolean).sort().pop() || null;

/** The step's own line: its title, and "Step N of 3" with three short bars (extra; the words carry it). @param {1 | 2 | 3} n @param {string} title */
function stepHead(n, title) {
  let bars = '';
  for (let i = 1; i <= 3; i++) bars += `<i${i <= n ? ' class="is-on"' : ''}></i>`;
  return '<div class="mad-step-head">'
    + `<h3 class="mad-step-title" id="mad-step-title" tabindex="-1">${title}</h3>`
    + `<p class="mad-step-count"><span class="mad-bars" aria-hidden="true">${bars}</span>Step ${n} of 3</p>`
    + '</div>';
}

/** A link as the coach sees it written: without its protocol (booked/store.js displayUrl). @param {string} url */
const bare = (url) => String(url || '').replace(/^https?:\/\//, '');

/**
 * Step 2 or 3 of the guide, the prototype's states g2, g2-pick, g2-none and g3. Pure, for the tests.
 * Step 2 has no Back (step 1 is done once he is connected); opened again from the Connected card, Close takes its place.
 * @param {2 | 3} step
 * @param {Array<{ name: string, url: string }> | null} links his sales call links; null while they are not known (still
 *   loading, or the read failed), when row 1 says where the link goes and shows no box
 * @param {number} picked which link the chooser shows
 * @param {boolean} reopened opened from "How to set up your ads"
 */
export function guideHtml(step, links, picked, reopened) {
  if (step === 3) {
    return '<div class="mad-guide">'
      + stepHead(3, 'Two things to watch for')
      + '<div class="mad-panel">'
      + `<div class="mad-avoid">${ICON.boost}<div>`
      + '<p class="mad-avoid-lead">When you boost a post on Instagram, pick your ad account.</p>'
      + '<p class="mad-avoid-more">Never pick the choice to boost without an ad account. Instagram may call it <b>“Without a Facebook ad account”</b> or <b>“Without a Meta ad account”</b>. FitOps can\'t see what those boosts cost.</p>'
      + '</div></div>'
      + `<div class="mad-avoid">${ICON.people}<div>`
      + '<p class="mad-avoid-lead">If someone else runs your ads, have them use your own ad account.</p>'
      + '<p class="mad-avoid-more">FitOps only reads ad accounts your Facebook login can see.</p>'
      + '</div></div>'
      + '</div>'
      + '<div class="mad-guide-foot">'
      + '<button type="button" class="btn-secondary" data-mad="back">Back</button>'
      + '<button type="button" class="btn-primary" data-mad="done">Done</button>'
      + '</div>'
      + '</div>';
  }
  const linkText = '<p class="mad-do-text" id="mad-link-h">In Ads Manager, paste your sales call link in the ad\'s Website URL box.</p>';
  let box = '';
  if (links && !links.length) {
    // No sales call link yet: the row still says where the link goes; only the box becomes the way to make one.
    box = '<div class="mad-none">'
      + '<p>Make a sales call link in Booked first.</p>'
      + '<button type="button" class="btn-secondary" data-mad="booked">Open Booked</button>'
      + '</div>';
  } else if (links) {
    const at = links[picked] ? picked : 0;
    const link = links[at];
    const top = links.length > 1
      ? '<label class="sr-only" for="mad-pick">Which sales call link</label>'
        + '<select class="mad-pick" id="mad-pick" data-mad="pick">'
        + links.map((l, i) => `<option value="${i}"${i === at ? ' selected' : ''}>${esc(l.name)}</option>`).join('')
        + '</select>'
      : `<span class="mad-link-name">${esc(link.name)}</span>`;
    box = `<div class="mad-link">${top}`
      + '<div class="mad-link-url">'
      + `<code id="mad-link-url">${esc(bare(link.url))}</code>`
      + `<button type="button" class="btn-xs btn-ghost mad-copy-link" data-mad="copy-link" aria-describedby="mad-link-h">${ICON.copy}<span aria-live="polite">Copy</span></button>`
      + `<a class="btn-xs btn-ghost" data-mad="open-link" href="${esc(link.url)}" target="_blank" rel="noopener" aria-label="Open the booking page">${ICON.external}</a>`
      + '</div>'
      + '<code class="mad-line-code" id="mad-link-shown" hidden></code>'
      + '</div>';
  }
  return '<div class="mad-guide">'
    + stepHead(2, 'Add your sales call link to every ad')
    + '<ol class="mad-do-list">'
    + `<li class="mad-do"><span class="mad-num" aria-hidden="true">1</span><div>${linkText}${box}</div></li>`
    + '<li class="mad-do"><span class="mad-num" aria-hidden="true">2</span><div>'
    + '<p class="mad-do-text" id="mad-line-h">Then paste this line in the URL parameters box, under Tracking.</p>'
    + `<button type="button" class="btn-secondary mad-line-btn" data-mad="copy-line" aria-describedby="mad-line-h">${ICON.copy}<span aria-live="polite">Copy the Meta line</span></button>`
    + '<code class="mad-line-code" id="mad-line-shown" hidden></code>'
    + '</div></li>'
    + '</ol>'
    + '<p class="mad-guide-note">FitOps then knows which booked calls came from which ad.</p>'
    + '<div class="mad-guide-foot">'
    + (reopened ? '<button type="button" class="btn-secondary" data-mad="close">Close</button>' : '')
    + '<button type="button" class="btn-primary" data-mad="next">Next</button>'
    + '</div>'
    + '</div>';
}

/**
 * The card's inside for one status, with the guide's open step drawn over states 2 and 3.
 * @param {any} s the status @param {number} now @param {string} tz
 * @param {{ step: 0 | 2 | 3, links: Array<{ name: string, url: string }> | null, picked: number, reopened: boolean }} [guide]
 * @returns {{ state: 1 | 2 | 3 | 4 | 5, html: string }}
 */
export function cardHtml(s, now, tz, guide) {
  const state = stateFor(s, now);
  if (guide && guide.step && (state === 2 || state === 3)) {
    return { state, html: header('email-domain-status--verified', 'Connected')
      + guideHtml(guide.step, guide.links, guide.picked, guide.reopened) };
  }
  const accounts = (s.accounts || []);
  // Several accounts on the login: the Ad account row names each.
  const names = accounts.map((/** @type {any} */ a) => esc(a.name || 'Ad account')).join('<br>');
  const updatedRow = (/** @type {any[]} */ from) => {
    const at = lastPulled(from);
    const w = at ? updatedWords(at, tz, now) : '';
    return w ? row('Updated', w) : '';
  };

  if (state === 1) {
    const setUp = s.setUp !== false;
    // The guide's step 1. Connect sits on the right of the bottom row, where Next and Done sit on the later steps,
    // with the hint beside it.
    return { state, html: header('email-domain-status--none', 'Not connected')
      + '<div class="mad-guide">'
      + stepHead(1, 'Connect your ad account')
      + '<p class="settings-note">Connect your ad account and FitOps reads what each ad spent every morning. Your dashboard then shows what your ads cost you for each booked call and each new client.</p>'
      + '<p class="settings-note">FitOps only reads your ad numbers. It can\'t change your ads or spend money.</p>'
      + '<div class="mad-guide-foot">'
      + `<p class="settings-note mad-hint">${setUp ? 'You\'ll log in with the Facebook account that runs your ads.' : 'The Facebook ads app is not set up in FitOps Admin yet.'}</p>`
      + `<button type="button" class="btn-primary" data-mad="connect"${setUp ? '' : ' disabled'}>Connect your ad account</button>`
      + '</div>'
      + '</div>' };
  }
  if (state === 2) {
    return { state, html: header('email-domain-status--verified', 'Connected')
      + '<div class="mad-panel">'
      + row('Ad account', names)
      + '<div class="mad-progress" role="status">'
      + '<p class="mad-progress-line"><span class="spinner-sm" aria-hidden="true"></span>Bringing in your past ad numbers. You can leave this page while it works.</p>'
      + '</div>'
      + '</div>'
      + ACTIONS + DISCONNECT + '</div>' };
  }
  if (state === 3) {
    // The parts always add up to the total: places other than Facebook and Instagram (Messenger, Audience Network)
    // are said whenever they spent anything.
    const sp = accounts.reduce((/** @type {any} */ t, /** @type {any} */ a) => {
      const x = a.spend90 || {};
      return { total: t.total + (Number(x.total) || 0), facebook: t.facebook + (Number(x.facebook) || 0),
        instagram: t.instagram + (Number(x.instagram) || 0), other: t.other + (Number(x.other) || 0) };
    }, { total: 0, facebook: 0, instagram: 0, other: 0 });
    const split = `${money(sp.facebook)} on Facebook and ${money(sp.instagram)} on Instagram`
      + (Math.round(sp.other) > 0 ? ` and ${money(sp.other)} in other places` : '');
    // Same 90 days and same ad accounts as the spend (meta-ads-status.js over90Days).
    const shown = accounts.reduce((/** @type {number} */ t, /** @type {any} */ a) => t + (Number(a.shown90) || 0), 0);
    const clicks = accounts.reduce((/** @type {number} */ t, /** @type {any} */ a) => t + (Number(a.linkClicks90) || 0), 0);
    return { state, html: header('email-domain-status--verified', 'Connected')
      + '<div class="mad-panel">'
      + row('Ad account', names)
      + updatedRow(accounts)
      + row('Spent in the last 90 days', money(sp.total), split)
      + row('Times shown in the last 90 days', fmt(shown))
      + row('Link clicks in the last 90 days', fmt(clicks))
      + '</div>'
      + ACTIONS
      + `<button class="settings-inline-link" data-mad="dashboard">See your ad numbers on your dashboard${ARROW}</button>`
      + DISCONNECT
      + '</div>'
      // The quiet way back into the guide, at step 2.
      + '<p class="mad-reopen"><button type="button" class="mad-guide-link" data-mad="guide">How to set up your ads</button></p>' };
  }
  if (state === 4) {
    const soonest = accounts.map((/** @type {any} */ a) => a.tokenExpiresAt).filter(Boolean).sort()[0];
    return { state, html: header('email-domain-status--pending', 'Log in again')
      + `<p class="settings-note">Facebook makes you log in again about every 60 days. Log in by ${longDay(dayIn(new Date(soonest), tz))} so your ad numbers keep updating.</p>`
      + '<div class="mad-panel">'
      + row('Ad account', names)
      + updatedRow(accounts)
      + '</div>'
      + ACTIONS
      + '<button class="btn-primary" data-mad="relogin">Log in to Facebook again</button>'
      + DISCONNECT
      + '</div>' };
  }
  // 5. The login has already stopped: no day to beat, and Updated is the last morning the numbers came in.
  const stopped = accounts.filter((/** @type {any} */ a) => a.status === 'reconnect' || expiresAt(a, now) <= 0);
  return { state, html: header('email-domain-status--pending', 'Log in again')
    + '<p class="settings-note">Facebook stopped letting FitOps read your ad numbers. Log in again so they start updating again.</p>'
    + '<div class="mad-panel">'
    + row('Ad account', names)
    + updatedRow(stopped)
    + '</div>'
    + ACTIONS
    + '<button class="btn-primary" data-mad="relogin">Log in to Facebook again</button>'
    + DISCONNECT
    + '</div>' };
}

/** Each card element gets its click listener once (the card is drawn again in place, never replaced). */
const _wired = new WeakSet();
let _msgWired = false;
let _seq = 0;
/** @type {ReturnType<typeof setTimeout> | null} */
let _recheck = null;

// The guide, kept in this module and never stored (see the note at the top).
/** @type {0 | 2 | 3} the open step; 0 is none */
let _guide = 0;
/** Opened again from "How to set up your ads": step 2 has Close in Back's place. */
let _reopened = false;
/** @type {Array<{ name: string, url: string }> | null} his sales call links, as step 2 last read them */
let _links = null;
let _picked = 0;
/** The state last drawn, so a connect from state 1 (not a log in again) opens the guide. */
let _shown = 0;
/** The status last drawn, so a step changed by a press draws again without asking the server. */
let _status = /** @type {any} */ (null);

/**
 * The sales call links step 2 offers: his active Booked links that add leads, at their best address (the branded one
 * when it is live). Booked is imported here, when step 2 opens, never at start-up. null when they cannot be read.
 * @returns {Promise<Array<{ name: string, url: string }> | null>}
 */
async function loadSalesLinks() {
  try {
    const [store, { addsLeadsOn }] = await Promise.all([import('./booked/store.js'), import('./booked/lead-links.js')]);
    await Promise.all([store.loadTypes(), store.loadLinkStyle()]);
    return store.getTypes()
      .filter((/** @type {any} */ t) => t && t.slug && t.active !== false && addsLeadsOn(t.data))
      .map((/** @type {any} */ t) => ({ name: String(t.data?.name || t.slug), url: store.pageUrl(t.slug) }));
  } catch (e) {
    console.warn('[meta-ads] sales call links:', /** @type {any} */ (e)?.message);
    return null;
  }
}

/**
 * Ask for the status and draw the card. Called when Settings opens on Integrations and on its tab click (app.js),
 * and again after a login or a disconnect. With step 2 open, the links are read again too.
 */
export async function initMetaAdsCard() {
  return refresh(true);
}

/** @param {boolean} freshLinks read the sales call links again when step 2 is open @param {boolean} [moved] a press changed the step */
async function refresh(freshLinks, moved = false) {
  const card = /** @type {HTMLElement | null} */ (document.getElementById('meta-ads-card'));
  if (!card) return;
  wire(card);
  const mine = ++_seq;
  if (_recheck) { clearTimeout(_recheck); _recheck = null; }
  let s;
  /** @type {Array<{ name: string, url: string }> | null | undefined} */
  let links;
  try {
    [s, links] = await Promise.all([
      callFunction('meta-ads-status', null, 'GET'),
      freshLinks && _guide === 2 ? loadSalesLinks() : Promise.resolve(undefined),
    ]);
  } catch (e) {
    // Refused (a team member) or unreachable: leave the card as it is, which is hidden until it has been told once.
    console.warn('[meta-ads] status:', /** @type {any} */ (e)?.message);
    return;
  }
  if (mine !== _seq) return; // a newer ask is on its way
  if (links !== undefined) { _links = links; if (!_links || _picked >= _links.length) _picked = 0; }
  if (!s || s.available !== true) {
    closeGuide();
    _shown = 0;
    _status = null;
    card.hidden = true;
    card.innerHTML = '';
    return;
  }
  _status = s;
  // An open step looks the same in states 2 and 3, so a recheck that changes neither leaves it alone: a redraw
  // would wipe an address shown to copy by hand, close the chooser and drop the focus, every 20 seconds.
  const st = stateFor(s, Date.now());
  const same = _guide && !moved && links === undefined && [2, 3].includes(st) && [2, 3].includes(_shown);
  const state = same ? st : draw(card, moved);
  card.hidden = false;
  if (state === 2) {
    _recheck = setTimeout(() => {
      _recheck = null;
      if (card.isConnected && !card.hidden) refresh(false);
    }, HISTORY_RECHECK_MS);
  }
}

function closeGuide() { _guide = 0; _reopened = false; }

/**
 * Draw the last status, with the open step. The guide closes on any state it does not draw over (1, 4 and 5).
 * @param {HTMLElement} card @param {boolean} moved a press changed the step: the new one rises in and takes the focus
 */
function draw(card, moved) {
  const now = Date.now();
  if (_guide && ![2, 3].includes(stateFor(_status, now))) closeGuide();
  const { state, html } = cardHtml(_status, now, getCoachTimezone(), { step: _guide, links: _links, picked: _picked, reopened: _reopened });
  card.innerHTML = html;
  _shown = state;
  if (moved) {
    const guide = card.querySelector('.mad-guide');
    if (guide) {
      guide.classList.add('is-entering');
      guide.addEventListener('animationend', () => guide.classList.remove('is-entering'), { once: true });
    }
    /** @type {HTMLElement | null} */ (card.querySelector('#mad-step-title') || card.querySelector('#meta-ads-title'))?.focus({ preventScroll: true });
  }
  return state;
}

/** A step changed by a press. @param {HTMLElement} card @param {0 | 2 | 3} step */
function go(card, step) {
  _guide = step;
  if (!step) _reopened = false;
  if (_status) draw(card, true);
}

/** @param {HTMLElement} card */
function wire(card) {
  if (!_wired.has(card)) {
    _wired.add(card);
    card.addEventListener('click', (e) => {
      const b = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-mad]'));
      if (!b || /** @type {HTMLButtonElement} */ (b).disabled) return;
      const act = b.dataset.mad;
      if (act === 'connect' || act === 'relogin') connect();
      else if (act === 'disconnect') disconnect(b);
      else if (act === 'dashboard') openView('dashboard');
      else if (act === 'next') go(card, 3);
      else if (act === 'back') go(card, 2);
      else if (act === 'done' || act === 'close') go(card, 0);
      else if (act === 'guide') { _guide = 2; _reopened = true; refresh(true, true); }
      else if (act === 'booked') openView('booked');
      else if (act === 'copy-link' && _links && _links[_picked]) copyLine(b, _links[_picked].url, card.querySelector('#mad-link-shown'));
      else if (act === 'copy-line') copyLine(b, AD_LINES.meta, card.querySelector('#mad-line-shown'));
    });
    // More than one sales call link: the chooser changes which one the box shows, copies and opens.
    card.addEventListener('change', (e) => {
      const sel = /** @type {HTMLSelectElement | null} */ (/** @type {HTMLElement} */ (e.target).closest('[data-mad="pick"]'));
      if (!sel || !_links) return;
      _picked = Number(sel.value) || 0;
      const link = _links[_picked];
      if (!link) return;
      const code = card.querySelector('#mad-link-url');
      if (code) code.textContent = bare(link.url);
      card.querySelector('[data-mad="open-link"]')?.setAttribute('href', link.url);
      const shown = /** @type {HTMLElement | null} */ (card.querySelector('#mad-link-shown'));
      if (shown) { shown.hidden = true; shown.textContent = ''; }
    });
  }
  if (!_msgWired) {
    _msgWired = true;
    window.addEventListener('message', (e) => {
      if (e.origin !== window.location.origin) return;
      const type = e.data && typeof e.data.type === 'string' ? e.data.type : '';
      if (!type.startsWith('meta-ads-')) return;
      if (type === 'meta-ads-connected') {
        // A first connect (from state 1) opens step 2, rising in with the focus on it like every other step change
        // (the Connect button pressed is gone); logging in again from state 4 or 5 does not start the guide over.
        if (_shown === 1) { _guide = 2; _reopened = false; refresh(true, true); }
        else initMetaAdsCard();
      }
      else if (type === 'meta-ads-failed') showToast(String(e.data.message || 'Facebook did not let FitOps connect. Try again.'), 'error');
    });
  }
}

/** Connect, or log in again: the same Facebook login in a popup. */
function connect() {
  // Synchronously, inside the click: see the note at the top.
  const popup = window.open('', 'meta-ads', 'width=600,height=720');
  if (!popup) {
    showToast('Pop-up blocked. Allow pop-ups for this site and try again.', 'error');
    return;
  }
  callFunction('meta-ads-connect-start', null, 'GET')
    .then((/** @type {any} */ res) => {
      if (!res || !res.url) throw new Error('Could not start the Facebook login. Try again.');
      popup.location.href = res.url;
    })
    .catch((/** @type {any} */ err) => {
      try { popup.close(); } catch { /* already closed */ }
      showToast(err?.message || 'Could not start the Facebook login. Try again.', 'error');
    });
}

/** @param {HTMLElement} btn */
async function disconnect(btn) {
  const ok = await showConfirm('Disconnect your ad account?\nFitOps stops bringing in your ad numbers.', { okLabel: 'Disconnect', danger: true });
  if (!ok) { btn.focus(); return; }
  try {
    await callFunction('meta-ads-disconnect', {}, 'POST');
  } catch (err) {
    showToast(/** @type {any} */ (err)?.message || 'Could not disconnect. Try again.', 'error');
    return;
  }
  await initMetaAdsCard();
}

/** A view (the dashboard, Booked), through the app's real view switcher (there is no global showView). @param {string} view */
function openView(view) {
  const nav = /** @type {any} */ (window)._navigation;
  if (nav && typeof nav.showView === 'function') nav.showView(view);
  else /** @type {HTMLElement | null} */ (document.querySelector(`.nav-btn[data-view="${view}"]`))?.click();
}
